import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export const StatCard = ({ label, value, accent = "#0A0A0A", testId }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white px-5 py-4" data-testid={testId}>
        <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{label}</div>
        <div className="mt-1.5 font-display text-3xl font-semibold tracking-tight" style={{ color: accent }}>{value}</div>
    </div>
);

export const Pill = ({ active, onClick, children, testId }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-3.5 py-1.5 rounded-full text-xs font-body border transition-all duration-300 ${
            active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-oth-ink2 border-oth-line/70 hover:border-oth-ink"
        }`}
        data-testid={testId}
    >{children}</button>
);

export const Tag = ({ children }) => (
    <span className="inline-block px-2 py-0.5 rounded-full bg-[#EFEFEA] text-[10px] uppercase tracking-[0.14em] text-oth-ink3">{children}</span>
);

export const TextInput = ({ label, value, onChange, type = "text", placeholder, hint, testId }) => (
    <label className="block">
        {label && <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">{label}</div>}
        <input
            type={type}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white border border-oth-line/70 rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
            data-testid={testId}
        />
        {hint && <div className="text-[11px] text-oth-ink3 mt-1 font-body">{hint}</div>}
    </label>
);

export const SelectInput = ({ label, value, onChange, options, testId }) => (
    <label className="block">
        {label && <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">{label}</div>}
        <select
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white border border-oth-line/70 rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
            data-testid={testId}
        >
            {options.map((o) => (
                <option key={String(o.value ?? o)} value={o.value ?? o}>{o.label ?? o}</option>
            ))}
        </select>
    </label>
);

export const Modal = ({ open, onClose, title, subtitle, children, maxWidth = "max-w-lg", testId }) => {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-4 sm:p-8"
                    onClick={onClose}
                    data-testid={testId}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.98 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full ${maxWidth} bg-[#FBFBF9] rounded-3xl border border-oth-line/70 shadow-2xl my-auto`}
                    >
                        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-oth-line/60">
                            <div>
                                <div className="font-display text-lg font-semibold tracking-tight">{title}</div>
                                {subtitle && <div className="text-xs font-body text-oth-ink3 mt-0.5">{subtitle}</div>}
                            </div>
                            <button onClick={onClose} className="w-8 h-8 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink2 hover:bg-white" data-testid={`${testId}-close`}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="px-6 py-5">{children}</div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export const PrimaryBtn = ({ onClick, children, disabled, testId, type = "button" }) => (
    <button type={type} onClick={onClick} disabled={disabled} className="btn-pill btn-pill-dark text-sm disabled:opacity-50" data-testid={testId}>{children}</button>
);

export const GhostBtn = ({ onClick, children, testId }) => (
    <button type="button" onClick={onClick} className="px-4 py-2 rounded-full border border-oth-line/70 text-sm font-body text-oth-ink2 hover:bg-white transition-colors" data-testid={testId}>{children}</button>
);

export const ChipMultiSelect = ({ label, options, selected, onChange, testId }) => {
    const toggle = (opt) => {
        const set = new Set(selected || []);
        set.has(opt) ? set.delete(opt) : set.add(opt);
        onChange(Array.from(set));
    };
    return (
        <div data-testid={testId}>
            {label && <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">{label}</div>}
            <div className="flex flex-wrap gap-2">
                {(options || []).map((o) => (
                    <Pill key={o} active={(selected || []).includes(o)} onClick={() => toggle(o)} testId={`${testId}-${o}`}>{o}</Pill>
                ))}
                {(!options || options.length === 0) && <span className="text-xs text-oth-ink3 italic font-body">No options yet — add them in Settings.</span>}
            </div>
        </div>
    );
};

export const EmptyState = ({ title, body, testId }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white p-10 text-center" data-testid={testId}>
        <div className="font-display text-lg font-semibold">{title}</div>
        <p className="mt-2 font-body text-sm text-oth-ink3 max-w-[380px] mx-auto">{body}</p>
    </div>
);
